import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import PageNav from '../components/PageNav'
import { to } from '../lib/nav'

import {
  ApiError,
  type StoredPdfInfo,
  apiFetch,
  fetchStoredPdf,
  generateReportPdf,
  getReportActions,
  getStoredPdfInfo,
} from '../lib/api'
import ReportDocument, { type RenderModel } from '../components/ReportDocument'

// The standalone report view: the document, plus the controls around it.
//
// The document itself is `components/ReportDocument`, shared with the report
// hub so the two cannot drift apart, and drawing the same model the PDF is
// printed from.

// Mirrors the server exactly. An earlier version of this interface guessed
// `name`/`size`; the server sends `family`/`size_pt`, so every style silently
// evaluated to `undefined` and the reports rendered in the page's default face
// at the default size. TypeScript could not catch it — the interface was the
// thing that was wrong.
export default function ReportViewPage() {
  const { studyId } = useParams()
  const [params] = useSearchParams()
  const [model, setModel] = useState<RenderModel | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showBindings, setShowBindings] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [archive, setArchive] = useState<StoredPdfInfo | null>(null)
  const [archiveUrl, setArchiveUrl] = useState<string | null>(null)
  const [showLive, setShowLive] = useState(true)

  // Rendered on demand rather than fetched from the share: the file only has to
  // exist when someone actually wants one.
  async function downloadPdf() {
    setDownloading(true)
    setError(null)
    try {
      const { url, filename } = await generateReportPdf(Number(studyId))
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.click()
      // Freed on the next tick; revoking immediately cancels the download in
      // some browsers before it has read the blob.
      setTimeout(() => URL.revokeObjectURL(url), 30_000)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setDownloading(false)
    }
  }

  useEffect(() => {
    apiFetch<RenderModel>(`/api/v1/studies/${studyId}/report/render`)
      .then(setModel)
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)))
  }, [studyId])

  // A signed report shows the document that was signed.
  //
  // Re-rendering it from current data would produce something that reads as the
  // signed report but is not: reference data moves, a physician's name changes,
  // a template is corrected. The archive on the share is the artefact the
  // signature was applied to, so for a signed study that is what is shown, and
  // the live render stays one click away and clearly labelled as such.
  //
  // Everything degrades to the live render — an unsigned study, a study whose
  // report was never exported, an unmounted share. The archive is preferred
  // where it exists, never required.
  useEffect(() => {
    setArchive(null)
    setArchiveUrl(null)
    let cancelled = false
    Promise.all([
      getReportActions(Number(studyId)).catch(() => null),
      getStoredPdfInfo(Number(studyId)).catch(() => null),
    ]).then(([actions, stored]) => {
      if (cancelled) return
      const signed = Boolean(actions?.already_signed)
      if (!signed || !stored?.has_stored_pdf || !stored.share_available) {
        setArchive(stored ?? null)
        return
      }
      setArchive(stored)
      setShowLive(false)
      fetchStoredPdf(Number(studyId))
        .then((url) => {
          if (!cancelled) setArchiveUrl(url)
        })
        .catch(() => {
          // The file is named but unreadable. Live render, with the reason.
          if (!cancelled) setShowLive(true)
        })
    })
    return () => {
      cancelled = true
    }
  }, [studyId])

  useEffect(() => () => {
    if (archiveUrl) URL.revokeObjectURL(archiveUrl)
  }, [archiveUrl])

  const diag = model?.diagnostics

  return (
    <main className="page">
      {/* `EchoReportViewer.aspx:17` — "Home  Back", the legacy's only viewer
          strip. Back falls through to the hub that opened this. */}
      <PageNav
        strip="reportView"
        studyId={studyId}
        backTo={to(`/studies/${studyId}/report`, params)}
      />

      {error && <p className="banner banner-error no-print">{error}</p>}

      {model && (
        <div className="report-toolbar no-print">
          <span className="muted" title={model.template_resolution?.source ?? undefined}>
            Template <code>{model.template}</code>
            {model.template_resolution?.matched_on &&
              ` · matched on ${model.template_resolution.matched_on.replace(/_/g, ' ')}`}
            {model.template_resolution?.substituted &&
              ` · substituted for ${model.template_resolution.substituted}`}
          </span>
          <label className="inline-label">
            <input
              type="checkbox"
              checked={showBindings}
              onChange={(e) => setShowBindings(e.target.checked)}
            />
            Show bindings
          </label>
          {(model.page_count ?? 1) > 1 && (
            <span className="muted">{model.page_count} pages</span>
          )}
          <button type="button" onClick={() => window.print()}>
            Print
          </button>
          <button type="button" onClick={downloadPdf} disabled={downloading}>
            {downloading ? 'Generating…' : 'Download PDF'}
          </button>
          {diag && diag.unresolved_field_count > 0 && (
            <span className="banner-warn-inline" title={diag.unresolved_fields.join(', ')}>
              {diag.unresolved_field_count} unresolved field
              {diag.unresolved_field_count === 1 ? '' : 's'} — shown blank
            </span>
          )}
          {diag && diag.warnings.length > 0 && (
            <span className="banner-warn-inline" title={diag.warnings.join(' · ')}>
              {diag.warnings.length} warning{diag.warnings.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
      )}

      {!model && !error && <p className="muted">Rendering…</p>}

      {/* Stated on the page, not tucked into the warnings count.
          An empty report is the one failure mode that looks like success: the
          demographics are real, the layout is correct, and there are no
          findings in it at all. Echo reporting moved to another system, and
          that system does not write its content back to this database, so a
          2024-or-later echo study renders complete and says nothing. */}
      {diag?.no_clinical_content && (
        <p className="banner banner-warn no-print">
          <b>This report has no clinical content.</b> All{' '}
          {diag.clinical_fields_bound} findings fields on the template are empty
          for this study, so what follows is the template and this patient's
          demographics — not a report. Check whether the study was reported in
          another system.
        </p>
      )}

      {archiveUrl && !showLive && (
        <>
          <p className="banner banner-ok no-print">
            This is the archived PDF — the document that was electronically
            signed, as it was signed. <code>{archive?.referenced_file}</code>
            <button type="button" className="link" onClick={() => setShowLive(true)}>
              Show the live render instead
            </button>
          </p>
          <iframe className="report-archive" src={archiveUrl} title="Signed report" />
        </>
      )}

      {archiveUrl && showLive && (
        <p className="banner banner-warn no-print">
          This study is signed, and this is a live render from current data — not
          the document that was signed. It can differ.
          <button type="button" className="link" onClick={() => setShowLive(false)}>
            Show the signed PDF
          </button>
        </p>
      )}

      {/* No "the signed document is not being shown" banner here, by product
          direction (14 August 2026) — the companion to the one removed from the
          report hub the same day.

          What it said: a stored PDF is recorded for this study and could not be
          opened, so what follows is drawn from this system's data instead. That
          matters most for a study reported outside this system, where the
          drawing is the template with demographics and no findings. The Stored
          PDF fieldset on the hub still names the file. */}

      {model && (!archiveUrl || showLive) && (
        <ReportDocument model={model} showBindings={showBindings} />
      )}
    </main>
  )
}

